import type { FormEvent, ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { formatMinor } from '~/api/client'
import { chartCss } from '~/charts/theme'
import type { Tone } from '~/charts/tokens'
import { BankOutcome } from '~/components/BankOutcome'
import { Combobox } from '~/components/Combobox'
import { CONNECT_CSS, ConnectFlow, Verdict, hasGuide } from '~/components/ConnectFlow'
import type { ConnectionTarget } from '~/components/ConnectFlow'
import { EntityChoice, useEntities } from '~/components/EntityChoice'
import { Card, CardHead } from '~/components/ui/Card'
import { Button } from '~/components/ui/Controls'
import { Modal } from '~/components/ui/Modal'
import type { ChipTone } from '~/components/ui/Status'
import { Monogram, StatusPill } from '~/components/ui/Status'
import { DataTable } from '~/components/ui/Table'
import { MANUAL_PATH } from '~/navigation'
import { CONNECTION_REACH } from '~/pages/Login'
import type {
  AddShape,
  AttributionChoice,
  CollectionSource,
  ConnectedCollection,
  Consent,
  ConsentSource,
  CredentialsSource,
  PendingConsent,
  SourceState,
} from '~/state/sources'
import { attributionId } from '~/state/bank-outcome'
import {
  collectionControl,
  useCollectionStanding,
  useRememberCollection,
  useRequestCollection,
  useSavedCollection,
} from '~/state/collections'
import type { SourceHealth } from '~/state/health'
import { useHealth } from '~/state/queries'
import {
  DONT_TRACK,
  attributionOf,
  confirmedAtNote,
  holdersOf,
  isFullyAttributed,
  refuseDraft,
  useAddItem,
  useAttributeAccounts,
  useBanksIn,
  useCheckSource,
  useRemoveItem,
  useSetItemEntity,
  useSources,
  useStartConsent,
} from '~/state/sources'

const PAGE_CSS = `
.nw-sources .nw-head { align-items: baseline; display: flex; flex-wrap: wrap; gap: var(--nw-space-2) var(--nw-space-3); justify-content: space-between; }
.nw-sources .nw-actions { display: flex; flex-wrap: wrap; gap: var(--nw-space-2); }
/* The control, and the rule it is held to directly under it — a button that
   refuses without the rule beside it reads as a button that is broken. */
.nw-sources .nw-head-actions { align-items: flex-end; display: flex; flex-direction: column; gap: 6px; }
.nw-sources .nw-head-actions > div { display: flex; flex-wrap: wrap; gap: var(--nw-space-2); }
.nw-sources .nw-collect-note { color: var(--nw-muted); font-size: var(--nw-text-chip); line-height: 1.45; margin: 0; max-width: 40ch; text-align: right; }
.nw-sources .nw-collect-note--refused { color: var(--nw-critical); }
.nw-sources .nw-refusal { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0 0 10px; }
.nw-sources .nw-collected { color: var(--nw-muted); font-size: var(--nw-text-cell); margin: 0 0 10px; }
.nw-sources .nw-note { color: var(--nw-warning); font-size: var(--nw-text-cell); margin: 0 0 10px; }
.nw-sources .nw-quiet { color: var(--nw-muted); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-stack); max-width: 78ch; }
.nw-sources .nw-none { color: var(--nw-body-ink); font-size: var(--nw-text-cell); margin: var(--nw-space-4) 0 var(--nw-space-3); }
.nw-sources .nw-waiting { border: var(--nw-hairline-width) solid var(--nw-warning); border-radius: var(--nw-radius-panel); margin: 0 0 var(--nw-space-4); padding: var(--nw-space-3) var(--nw-space-4); }
.nw-sources .nw-value { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: var(--nw-text-chip); overflow-wrap: anywhere; }
.nw-sources fieldset { border: 0; display: grid; gap: var(--nw-space-3); grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); margin: var(--nw-space-4) 0 var(--nw-space-3); padding: 0; }
.nw-sources fieldset label { color: var(--nw-faint); display: flex; flex-direction: column; font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); gap: var(--nw-space-2); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-sources fieldset :where(input, select) { width: 100%; }
/* The entity choice renders its own label beside its select rather than
   wrapping it, so the column layout every other field gets from its label has
   to be said here instead. */
.nw-sources fieldset .nw-field { display: flex; flex-direction: column; gap: var(--nw-space-2); }
.nw-sources small { color: var(--nw-muted); font-size: var(--nw-text-chip); font-weight: var(--nw-weight-body); letter-spacing: 0; text-transform: none; }

/* The row: a tile, the connection's name, and what is under it. */
.nw-sources .nw-conn { align-items: center; display: flex; gap: var(--nw-space-3); }
.nw-sources .nw-conn b { display: block; font-size: var(--nw-text-cell); font-weight: var(--nw-weight-medium); }

/* Every login a connection-holding source has, before one of them is opened. */
.nw-connections-list { display: flex; flex-direction: column; gap: var(--nw-space-3); list-style: none; margin: 0; padding: 0; }
.nw-connections-list li { align-items: center; display: flex; gap: var(--nw-space-3); justify-content: space-between; }
.nw-connections-list li span { display: flex; flex-direction: column; gap: 2px; }
.nw-connections-list li b { font-size: var(--nw-text-cell); font-weight: var(--nw-weight-medium); }
.nw-sources .nw-conn small { display: block; }
.nw-sources .nw-when { color: var(--nw-muted); display: block; font-size: var(--nw-text-chip); margin-top: 2px; }

/* Each thing needing a hand, and the one control that goes to it. */
.nw-sources .nw-todo { display: flex; flex-direction: column; gap: var(--nw-space-3); list-style: none; margin: 0; padding: 0; }
.nw-sources .nw-todo li { align-items: center; border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule); display: flex; gap: var(--nw-space-4); justify-content: space-between; padding-bottom: var(--nw-space-3); }
.nw-sources .nw-todo li:last-child { border-bottom: 0; padding-bottom: 0; }
.nw-sources .nw-todo p { font-size: var(--nw-text-cell); line-height: 1.5; margin: 0; max-width: 72ch; }
.nw-sources .nw-todo b { display: block; font-weight: var(--nw-weight-strong); }

/* The chooser: one tile per source, and a source that cannot be connected says
   so in the tile rather than behind a button that opens onto a refusal. */
.nw-sources-offer { display: grid; gap: var(--nw-space-3); grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); list-style: none; margin: 0; padding: 0; }
.nw-sources-offer li { border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: var(--nw-radius-panel); display: flex; flex-direction: column; gap: var(--nw-space-2); padding: var(--nw-space-4); }
.nw-sources-offer b { font-size: var(--nw-text-cell); font-weight: var(--nw-weight-strong); }
.nw-sources-offer p { color: var(--nw-muted); font-size: var(--nw-text-chip); line-height: 1.5; margin: 0; }
.nw-sources-offer .nw-btn { margin-top: auto; }

/* This page is walked once, usually on a phone, and an attribution row whose
   account scrolls out of sight of its own menu cannot be answered. */
@media (max-width: 600px) {
  .nw-sources .nw-data, .nw-sources tbody, .nw-sources tr, .nw-sources td { display: block; width: 100%; }
  .nw-sources thead { display: none; }
  .nw-sources tr { border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule); padding: var(--nw-space-2) 0; }
  .nw-sources td { border: 0; height: auto; padding: 3px 0; }
  .nw-sources td[data-label]::before { color: var(--nw-muted); content: attr(data-label) ' '; font-size: var(--nw-text-meta); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
  .nw-sources .nw-num { text-align: left; }
  .nw-sources .nw-todo li { align-items: flex-start; flex-direction: column; gap: var(--nw-space-2); }
  .nw-sources .nw-head-actions { align-items: flex-start; width: 100%; }
  .nw-sources .nw-collect-note { text-align: left; }
}
`

const CONNECTION_COLUMNS = [
  { id: 'source', label: 'Source' },
  { id: 'kind', label: 'Kind', width: '150px' },
  { id: 'status', label: 'Status', width: '210px' },
  { id: 'value', label: 'Value', width: '170px', align: 'right' as const },
  { id: 'action', label: ' ', width: '150px' },
]

/**
 * Country is shown rather than implied. The same brand is published in several
 * markets — Wise and Revolut are each listed in Denmark, the Netherlands and
 * Estonia — so a household holding two of them sees two rows that are otherwise
 * word for word identical, and has to know which one lapsed.
 */
const CONSENT_COLUMNS = [
  { id: 'bank', label: 'Bank', width: '220px' },
  { id: 'country', label: 'Country', width: '160px' },
  { id: 'whose', label: 'Whose', width: '140px' },
  { id: 'approval', label: 'Approval', width: '240px' },
  { id: 'accounts', label: 'Accounts' },
]

const ATTRIBUTION_COLUMNS = [
  { id: 'account', label: 'Account' },
  { id: 'whose', label: 'Whose is it', width: '300px' },
]

const WALLET_COLUMNS = [
  { id: 'what', label: 'What', width: '120px' },
  { id: 'value', label: 'Address' },
  { id: 'entity', label: 'Whose', width: '170px' },
  { id: 'actions', label: ' ', width: '150px' },
]

/**
 * How a source is connected, which is the only kind this product actually knows
 * about it.
 *
 * The mockups sorted them into Bank, Broker, Exchange and Wallet, which is a
 * fact about the institution and not one this codebase holds anywhere. What it
 * does hold is the shape of the connection, and that is what decides everything
 * else on the row.
 */
const KIND_LABEL: Readonly<Record<string, string>> = {
  consent: 'Bank approval',
  credentials: 'Credential',
  collection: 'Watched list',
  unknown: 'Unrecognised',
}

export function Sources(): ReactElement {
  const sources = useSources()
  const health = useHealth()
  const [offering, setOffering] = useState(false)
  // A bare source id opens whatever the old singleton dialog was; a source
  // that holds connections needs to say which one too — an existing row to
  // renew, or `'new'` for a fresh login. See `ConnectionTarget`.
  const [opened, setOpened] = useState<{ id: string; connection?: ConnectionTarget } | null>(null)
  const collected = useSavedCollection()
  const remember = useRememberCollection()

  const all = sources.data?.sources ?? []
  const connected = all.filter((source) => source.configured)
  const open = all.find((source) => source.id === opened?.id)
  const openSource = (id: string): void => setOpened({ id })
  const openConnection = (id: string, connection: ConnectionTarget): void => setOpened({ id, connection })

  return (
    <main className="nw nw-sources">
      <style>{chartCss + PAGE_CSS + CONNECT_CSS}</style>
      <div className="nw-head">
        <h1>Sources</h1>
        <HeadActions collectable={connected.length > 0} onAdd={() => setOffering(true)} />
      </div>
      {sources.data && (
        <p className="nw-sub">
          {connected.length === 0
            ? 'Nothing is connected yet.'
            : `${connected.length} of ${all.length} connected${
                health.data?.date === null || health.data?.date === undefined
                  ? ''
                  : ` · last valued ${health.data.date}`
              }`}
        </p>
      )}
      {/* The one sentence about what a connection may do, read from where the
          way in says it. Two screens disagreeing about what this product can
          reach would be worse than one of them being wrong. */}
      <p className="nw-quiet">
        {CONNECTION_REACH} Nothing typed here is ever shown again — a credential is replaced rather
        than read back.
      </p>

      <BankOutcome />

      {/* What connecting just started, said on the page the dialog closed onto.
          A source that is being collected and a source that will be collected
          tonight look identical until somebody is told which. */}
      {collected !== null && (
        <p className="nw-collected" role="status">
          {collected.note}
        </p>
      )}

      {sources.isPending && <p className="nw-empty">Loading…</p>}
      {sources.error && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Could not read your sources: {sources.error.message}</span>
        </p>
      )}
      {/* Every row below describes a connection; none of them says whether last
          night's pricing itself came back. Without this, a household reads
          "connected" everywhere and never learns the figures rest on a rate
          that never arrived. */}
      {health.data?.priceRunError !== null && health.data?.priceRunError !== undefined && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Last night's pricing run did not finish: {health.data.priceRunError}</span>
        </p>
      )}

      {all.map((source) =>
        source.kind === 'consent'
          ? source.pending.map((pending) => (
              <Attribution key={pending.reference} source={source} pending={pending} />
            ))
          : null,
      )}

      <Attention sources={all} onOpen={openSource} />

      {sources.data && connected.length === 0 ? (
        <Card>
          <CardHead
            title="Nothing is connected yet"
            lede="No source has been set up, so no figure on this dashboard is anybody's money yet. Adding one is the whole of the setup."
          />
          <div className="nw-actions">
            <Button kind="primary" onClick={() => setOffering(true)}>
              Add a source
            </Button>
          </div>
        </Card>
      ) : (
        connected.length > 0 && (
          <Connections
            sources={connected}
            health={health.data?.sources ?? []}
            currency={health.data?.baseCurrency ?? 'DKK'}
            onOpen={openSource}
          />
        )
      )}

      {offering && (
        <Chooser
          sources={all}
          onClose={() => setOffering(false)}
          onChoose={(id) => {
            setOffering(false)
            openSource(id)
          }}
        />
      )}

      {open !== undefined && (
        <Connect
          source={open}
          connection={opened?.connection}
          onSelectConnection={(target) => openConnection(open.id, target)}
          onClose={(started) => {
            setOpened(null)
            if (started !== undefined) remember({ collection: started })
          }}
        />
      )}
    </main>
  )
}

/**
 * Adding a source, and asking the ones already there to report now.
 *
 * The request joins the queue the nightly run works through, so pressing it
 * twice cannot start two runs against the same positions — the second press
 * finds the household already in the queue and says so. Nothing is collected
 * while the request is open: it comes back as soon as the queue has taken the
 * household, and the standing is what the page then follows.
 *
 * Offered only once something is connected. A household with nothing to collect
 * pressing a button that collects nothing learns nothing from it.
 */
function HeadActions({
  collectable,
  onAdd,
}: {
  collectable: boolean
  onAdd: () => void
}): ReactElement {
  const standing = useCollectionStanding()
  const request = useRequestCollection()
  const control = collectionControl(standing.data)
  const offered = collectable && standing.data?.available === true
  const refusal = request.error?.message

  return (
    <div className="nw-head-actions">
      <div>
        {offered && (
          <Button
            disabled={control.disabled || request.isPending}
            onClick={() => request.mutate()}
          >
            {request.isPending ? 'Asking…' : control.label}
          </Button>
        )}
        <Button kind="primary" onClick={onAdd}>
          Add a source
        </Button>
      </div>
      {offered && (
        <p
          className={
            refusal === undefined ? 'nw-collect-note' : 'nw-collect-note nw-collect-note--refused'
          }
          role="status"
        >
          {refusal ?? control.note}
        </p>
      )}
    </div>
  )
}

/**
 * Whichever way this source is connected, opened over the page.
 *
 * Hand-entered holdings are the one source with no dialog: they have a page of
 * their own that already values them, and a second door onto the same rows is a
 * second place for the two of them to disagree.
 */
function Connect({
  source,
  connection,
  onSelectConnection,
  onClose,
}: {
  source: SourceState
  /** Which connection this is about; see `ConnectionTarget`. Ignored outside `kind: 'credentials'`. */
  connection?: ConnectionTarget
  /** Switches the same open dialog from the list below to one particular connection. */
  onSelectConnection: (connection: ConnectionTarget) => void
  onClose: (collected?: ConnectedCollection) => void
}): ReactElement | null {
  if (source.unavailable !== undefined) {
    return <Unavailable source={source} onClose={onClose} />
  }
  if (source.kind === 'consent') return <BankDialog source={source} onClose={onClose} />
  if (source.kind === 'collection') {
    if (source.add.kinds === undefined) return null
    return <WalletsDialog source={source} onClose={onClose} />
  }
  if (source.kind === 'credentials' && hasGuide(source)) {
    // Opened with no particular connection named — "Manage", on a source that
    // already holds one or more — shows the list first, rather than assuming
    // the household meant to add a new one on top of what is already there.
    if (source.connections !== undefined && connection === undefined) {
      return (
        <ConnectionsList source={source} onSelect={onSelectConnection} onClose={() => onClose()} />
      )
    }
    // Reached either because the source has no connections yet — "Add a
    // source", or the empty state's own button, both of which mean a fresh
    // one — or because the list above was used to pick one.
    const target = connection ?? (source.connections !== undefined ? 'new' : undefined)
    return <ConnectFlow source={source} connection={target} onClose={onClose} />
  }
  return null
}

/**
 * Every connection a source holds, before any one of them is opened.
 *
 * A separate screen from `ConnectFlow` rather than a first step inside it,
 * because the two answer different questions: this says *which* login, that
 * one says what to do with it. Folding them into one flow would mean the
 * walk's own back button had to undo a choice that was not a step of the walk.
 */
function ConnectionsList({
  source,
  onSelect,
  onClose,
}: {
  source: CredentialsSource
  onSelect: (connection: ConnectionTarget) => void
  onClose: () => void
}): ReactElement {
  const rows = source.connections ?? []
  return (
    <Modal
      title={source.label}
      labelledBy={`nw-connections-${source.id}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button kind="primary" onClick={() => onSelect('new')}>
            Add a connection
          </Button>
        </>
      }
    >
      {rows.length === 0 ? (
        <p className="nw-none">Nothing is connected yet.</p>
      ) : (
        <ul className="nw-connections-list">
          {rows.map((row) => (
            <li key={row.id}>
              <span>
                <b>{row.label}</b>
                {row.attention !== undefined ? (
                  <small className="nw-refusal">{row.attention}</small>
                ) : (
                  <small>{row.configured ? 'Connected' : 'Not yet configured'}</small>
                )}
                {row.credentialConfirmedAt !== undefined && (
                  <small>{confirmedAtNote(row.credentialConfirmedAt)}</small>
                )}
              </span>
              <Button onClick={() => onSelect({ id: row.id, label: row.label })}>
                {row.attention === undefined ? 'Manage' : 'Fix'}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}

function Unavailable({
  source,
  onClose,
}: {
  source: SourceState
  onClose: () => void
}): ReactElement {
  return (
    <Modal
      title={source.label}
      labelledBy={`nw-unavailable-${source.id}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <p className="nw-none">{source.unavailable}</p>
    </Modal>
  )
}

/**
 * What needs doing, named, with the control that goes and does it.
 *
 * A sentence saying a connection has lapsed and nothing to press is the shape
 * this page had before: the owner is told, and then has to work out for
 * themselves which of six forms below is the one to fill in.
 */
function Attention({
  sources,
  onOpen,
}: {
  sources: readonly SourceState[]
  onOpen: (id: string) => void
}): ReactElement | null {
  const items = sources.flatMap((source) => needsAHand(source))
  if (items.length === 0) return null

  return (
    <Card>
      <CardHead
        title={
          items.length === 1 ? 'One connection needs you' : `${items.length} connections need you`
        }
      />
      <ul className="nw-todo">
        {items.map((item) => (
          <li key={item.key}>
            <p>
              <b>{item.label}</b>
              {item.why}
            </p>
            {item.id === 'private' ? (
              <a className="nw-btn" href={MANUAL_PATH}>
                {item.action}
              </a>
            ) : (
              <Button onClick={() => onOpen(item.id)}>{item.action}</Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  )
}

interface ToDo {
  key: string
  id: string
  label: string
  why: string
  action: string
}

/**
 * The two ways a source asks for something: the sentence the server hands back,
 * and a bank approval running out.
 *
 * An expiring consent is not `attention` on the server — nothing has failed
 * yet — and it is the one failure that can be prevented rather than reported,
 * so it is the one most worth a place here.
 */
function needsAHand(source: SourceState): ToDo[] {
  const items: ToDo[] = []
  const action = source.renew ?? 'Reconnect'

  if (source.attention !== undefined) {
    items.push({
      key: `${source.id}:attention`,
      id: source.id,
      label: source.label,
      why: source.attention,
      action: source.id === 'private' ? 'Value them' : action,
    })
  }

  if (source.kind === 'consent') {
    for (const consent of source.consents) {
      if (consent.state !== 'expiring') continue
      items.push({
        key: `${source.id}:${consent.bank}:${consent.holder}`,
        id: source.id,
        label: `${source.label} — ${consent.bank}`,
        why: `The approval runs out in ${consent.daysRemaining} days. European rules cap it at 180, so it has to be given again.`,
        action,
      })
    }
  }

  return items
}

function Connections({
  sources,
  health,
  currency,
  onOpen,
}: {
  sources: readonly SourceState[]
  health: readonly SourceHealth[]
  currency: string
  onOpen: (id: string) => void
}): ReactElement {
  const reported = useMemo(() => new Map(health.map((one) => [one.source, one])), [health])

  return (
    <Card pad="flush">
      <CardHead
        title="Connected"
        lede="Value is what each source contributed at the last import. A source that stops importing keeps its last figures and says so here."
      />
      <DataTable columns={CONNECTION_COLUMNS} label="Connected sources">
        {sources.map((source) => (
          <Row
            key={source.id}
            source={source}
            reported={reported.get(source.collector ?? source.id)}
            currency={currency}
            onOpen={onOpen}
          />
        ))}
      </DataTable>
    </Card>
  )
}

function Row({
  source,
  reported,
  currency,
  onOpen,
}: {
  source: SourceState
  reported: SourceHealth | undefined
  currency: string
  onOpen: (id: string) => void
}): ReactElement {
  const standing = standingOf(source, reported)

  return (
    <tr>
      <td data-label="Source">
        <span className="nw-conn">
          <Monogram initials={initialsOf(source.label)} tone={toneOf(source)} size="sm" />
          <span>
            <b>{source.label}</b>
            <small>{detailOf(source, reported)}</small>
          </span>
        </span>
      </td>
      <td data-label="Kind">{KIND_LABEL[source.kind] ?? KIND_LABEL['unknown']}</td>
      <td data-label="Status">
        <StatusPill tone={standing.tone} label={standing.label} glyph />
        <span className="nw-when">{standing.when}</span>
      </td>
      <td data-label="Value" className="nw-num">
        {reported === undefined || reported.valueMinor === null
          ? '—'
          : formatMinor(reported.valueMinor, currency)}
        {reported !== undefined && reported.valueMinor !== null && reported.unpriced > 0 && (
          <small>{reported.unpriced} not included</small>
        )}
      </td>
      <td>
        {source.id === 'private' ? (
          <a className="nw-btn" href={MANUAL_PATH}>
            Open
          </a>
        ) : (
          <Button onClick={() => onOpen(source.id)}>
            {source.attention === undefined ? 'Manage' : (source.renew ?? 'Reconnect')}
          </Button>
        )}
      </td>
    </tr>
  )
}

/**
 * Three answers, and the one that matters is the middle one.
 *
 * Carried forward is a source that is not reporting while every figure it once
 * produced goes on being counted, which is the standing this page exists to
 * make visible — it is invisible everywhere else, because the total still adds
 * up.
 */
function standingOf(
  source: SourceState,
  reported: SourceHealth | undefined,
): { tone: ChipTone; label: string; when: string } {
  if (source.attention !== undefined) {
    return { tone: 'failed', label: 'Needs attention', when: lastRan(reported) }
  }
  const expiring = source.kind === 'consent' ? soonestExpiring(source.consents) : undefined
  if (expiring !== undefined) {
    // Ahead of `reported?.tone`, because a consent running out is worth
    // saying even on a source that collected cleanly last night — it is the
    // one failure here that can still be prevented rather than only reported.
    return {
      tone: 'caution',
      label: `Expires in ${expiring.daysRemaining} days`,
      when: lastRan(reported),
    }
  }
  if (reported?.tone === 'stale') {
    return { tone: 'caution', label: 'Carried forward', when: lastRan(reported) }
  }
  if (reported?.tone === 'failed') {
    return { tone: 'failed', label: 'Did not report', when: lastRan(reported) }
  }
  return { tone: 'live', label: 'Reporting', when: lastRan(reported) }
}

/**
 * The consent nearest to running out, if any is close enough to say so.
 *
 * Read fresh off `daysRemaining` every render rather than a flag anybody has
 * to clear: the moment the bank grants a fresh approval, `validUntil` moves
 * out, `consentStatus` recomputes, and this chip is gone on its own — nothing
 * here can go stale the way a dismissed banner would.
 */
function soonestExpiring(consents: readonly Consent[]): Consent | undefined {
  return consents
    .filter((consent) => consent.state === 'expiring')
    .sort((a, b) => a.daysRemaining - b.daysRemaining)[0]
}

/**
 * The day, not the hour.
 *
 * Nothing in any response carries the clock time of a run: `source_result` is
 * keyed to the date being valued, and the run's own timestamps are never served.
 * "11 minutes ago" would have to be invented.
 */
function lastRan(reported: SourceHealth | undefined): string {
  if (reported?.lastCollected == null) return 'Never reported for itself'
  return `Reported for ${reported.lastCollected}`
}

function detailOf(source: SourceState, reported: SourceHealth | undefined): string {
  if (source.kind === 'consent') {
    const names = source.consents.map((consent) => `${consent.bank} · ${consent.holder}`)
    if (names.length > 0) return names.join(', ')
  }
  if (source.kind === 'collection' && source.items.length > 0) {
    return `${source.items.length} ${source.items.length === 1 ? 'entry' : 'entries'}`
  }
  if (reported === undefined) return 'Nothing recorded yet'
  return reported.unpriced > 0
    ? `${reported.summary} · ${reported.unpriced} without a price`
    : reported.summary
}

function initialsOf(label: string): string {
  return label.slice(0, 2).toUpperCase()
}

function toneOf(source: SourceState): Tone | 'neutral' {
  if (source.kind === 'consent') return 'cash'
  if (source.id === 'private') return 'property'
  if (source.kind === 'collection') return 'crypto'
  return 'equity'
}

/**
 * Every source, and what it would take to connect it.
 *
 * Deliberate rather than permanent: six forms standing open on one page is six
 * invitations to type a credential into the wrong one, and five of them were
 * badged as failures on a household four seconds old.
 */
function Chooser({
  sources,
  onClose,
  onChoose,
}: {
  sources: readonly SourceState[]
  onClose: () => void
  onChoose: (id: string) => void
}): ReactElement {
  return (
    <Modal
      title="Add a source"
      lede="Each of these is connected once and keeps reporting on its own."
      labelledBy="nw-sources-offer"
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <ul className="nw-sources-offer">
        {sources.map((source) => (
          <li key={source.id}>
            <b>{source.label}</b>
            <p>{source.unavailable ?? source.note ?? KIND_LABEL[source.kind]}</p>
            <Offer source={source} onChoose={onChoose} />
          </li>
        ))}
      </ul>
    </Modal>
  )
}

function Offer({
  source,
  onChoose,
}: {
  source: SourceState
  onChoose: (id: string) => void
}): ReactElement {
  if (source.unavailable !== undefined) {
    return <StatusPill tone="manual" label="Not available" />
  }
  if (source.id === 'private') {
    return (
      <a className="nw-btn" href={MANUAL_PATH}>
        Enter one by hand
      </a>
    )
  }
  return (
    <Button onClick={() => onChoose(source.id)}>
      {source.configured ? 'Manage' : 'Connect'}
    </Button>
  )
}

/**
 * The addresses this household watches, and the one being added, with what the
 * chain says about it before it is added.
 *
 * The check is the point of the dialog. A mistyped address that still parses is
 * a wallet holding nothing, reported nightly as a wallet holding nothing, and
 * there is no way to tell it from the real thing without asking a chain.
 */
function WalletsDialog({
  source,
  onClose,
}: {
  source: CollectionSource
  onClose: () => void
}): ReactElement {
  const remove = useRemoveItem(source.id)

  return (
    <Modal
      title={source.label}
      lede={source.note}
      labelledBy={`nw-wallets-${source.id}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {source.items.length === 0 ? (
        <p className="nw-none">No address is being watched yet.</p>
      ) : (
        <DataTable columns={WALLET_COLUMNS} label={`What ${source.label} watches`}>
          {source.items.map((item) => (
            <tr key={item.id}>
              <td data-label="What">{item.label}</td>
              <td data-label="Address" className="nw-value">
                {item.secret === true ? 'stored — never shown again' : (item.value ?? '')}
              </td>
              <td data-label="Whose">
                {source.attributable === true && <WalletEntity source={source} item={item} />}
              </td>
              <td>
                <Button disabled={remove.isPending} onClick={() => remove.mutate(item.id)}>
                  Stop watching
                </Button>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      {remove.error && <p className="nw-refusal">{remove.error.message}</p>}

      <AddItem source={source} shape={source.add} />
    </Modal>
  )
}

/**
 * Whose a watched wallet is, and the one control that changes it.
 *
 * Shown even where nobody has said — it is the entity this wallet actually
 * reports under right now, the household's default and not a blank, because a
 * row that reads as unanswered when it is quietly resolving to the default
 * anyway is the exact confusion PRO-226 exists to end.
 */
function WalletEntity({
  source,
  item,
}: {
  source: CollectionSource
  item: CollectionSource['items'][number]
}): ReactElement {
  const setEntity = useSetItemEntity(source.id)

  return (
    <>
      <select
        aria-label={`Whose is ${item.label} ${item.value ?? ''}?`}
        value={item.entityKey ?? ''}
        disabled={setEntity.isPending}
        onChange={(e) => {
          const entityKey = e.target.value
          if (entityKey !== '') setEntity.mutate({ itemId: item.id, entityKey })
        }}
      >
        {item.entityKey === undefined && <option value="">Choose…</option>}
        <EntityOptions />
      </select>
      {setEntity.error && <small className="nw-error">{setEntity.error.message}</small>}
    </>
  )
}

/** The household's own entities, as `<option>`s — shared so the menu never drifts from `EntityChoice`'s own list. */
function EntityOptions(): ReactElement {
  const { data, isPending } = useEntities()
  const entities = data?.entities ?? []
  if (isPending) return <option value="">Loading…</option>
  return (
    <>
      {entities.map((entity) => (
        <option key={entity.key} value={entity.key}>
          {entity.name}
        </option>
      ))}
    </>
  )
}

/**
 * One add form, with the provider asked about the entry before it is kept where
 * the source can be asked at all.
 *
 * Its menu opens on *Choose…*. One menu that is always answered cannot be the
 * one that quietly defaults.
 */
function AddItem({ source, shape }: { source: CollectionSource; shape: AddShape }): ReactElement {
  const addItem = useAddItem(source.id)
  const check = useCheckSource(source.id)
  const [kind, setKind] = useState('')
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [refusal, setRefusal] = useState<string | null>(null)
  const taken = source.items.map((item) => item.value ?? '')

  const clear = (): void => {
    setDraft({})
    setKind('')
    setRefusal(null)
    check.reset()
  }

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    const refused = refuseDraft(shape, kind, draft, taken)
    if (refused !== undefined) {
      setRefusal(refused)
      return
    }
    setRefusal(null)
    addItem.mutate({ ...(shape.kinds ? { kind } : {}), values: draft }, { onSuccess: clear })
  }

  const chosen = shape.kinds?.find((candidate) => candidate.key === kind)
  const ready = kind !== '' && (draft['value'] ?? '').trim() !== ''

  return (
    <form onSubmit={submit} aria-label={shape.label}>
      <fieldset>
        {shape.kinds !== undefined && (
          <label htmlFor={`${source.id}-kind`}>
            {shape.kindLabel ?? 'Kind'}
            <select id={`${source.id}-kind`} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="">Choose…</option>
              {shape.kinds.map((candidate) => (
                <option key={candidate.key} value={candidate.key}>
                  {candidate.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {shape.fields.map((field) =>
          field.type === 'entity' ? (
            <div key={field.key}>
              <EntityChoice
                id={`${source.id}-${field.key}`}
                label={field.label}
                placeholder="The household’s default"
                value={draft[field.key] ?? ''}
                onChange={(entityKey) =>
                  setDraft((current) => ({ ...current, [field.key]: entityKey }))
                }
              />
              {field.help !== undefined && <small>{field.help}</small>}
            </div>
          ) : (
            <label key={field.key} htmlFor={`${source.id}-${field.key}`}>
              {field.label}
              {field.required ? '' : ' (optional)'}
              <input
                id={`${source.id}-${field.key}`}
                type={field.type === 'number' ? 'text' : field.type}
                inputMode={field.type === 'number' ? 'decimal' : undefined}
                autoComplete="off"
                placeholder={chosen?.placeholder ?? field.placeholder}
                value={draft[field.key] ?? ''}
                onChange={(e) => setDraft((current) => ({ ...current, [field.key]: e.target.value }))}
              />
              {field.help !== undefined && <small>{field.help}</small>}
            </label>
          ),
        )}
      </fieldset>

      {refusal !== null && <p className="nw-refusal">{refusal}</p>}
      {addItem.error && <p className="nw-refusal">{addItem.error.message}</p>}
      {addItem.data?.collection !== undefined && (
        <p className="nw-collected" role="status">
          {addItem.data.collection.note}
        </p>
      )}

      <div className="nw-actions">
        {source.checkable === true && (
          <Button
            disabled={!ready || check.isPending}
            onClick={() => check.mutate({ kind, values: draft })}
          >
            {check.isPending ? 'Reading the chain…' : 'Read it from the chain'}
          </Button>
        )}
        <Button type="submit" kind="primary" disabled={addItem.isPending}>
          {shape.label}
        </Button>
      </div>

      <Verdict verdict={check.data} failure={check.error} />
    </form>
  )
}

function BankDialog({
  source,
  onClose,
}: {
  source: ConsentSource
  onClose: () => void
}): ReactElement {
  return (
    <Modal
      title={source.label}
      lede={source.note}
      labelledBy={`nw-banks-${source.id}`}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {source.attention !== undefined && <p className="nw-note">{source.attention}</p>}

      {source.consents.length === 0 ? (
        <p className="nw-none">No bank is connected yet.</p>
      ) : (
        <DataTable columns={CONSENT_COLUMNS} label="Bank approvals">
          {source.consents.map((consent) => (
            <tr key={`${consent.country}/${consent.bank}/${consent.holder}/${consent.validUntil}`}>
              <td data-label="Bank">{consent.bank}</td>
              <td data-label="Country">{countryName(consent.country)}</td>
              <td data-label="Whose">{consent.holder === 'business' ? 'Business' : 'Personal'}</td>
              <td data-label="Approval">{describeConsent(consent.state, consent.daysRemaining)}</td>
              <td data-label="Accounts">
                {consent.accounts
                  .filter((account) => !account.excluded)
                  .map((account) => account.name)
                  .join(', ') || 'None tracked'}
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <ConnectBank source={source} />
    </Modal>
  )
}

function describeConsent(state: ConsentSource['consents'][number]['state'], days: number): string {
  if (state === 'expired') return 'Expired — reconnect to resume'
  if (state === 'expiring') return `Expires in ${days} days`
  return `Valid for ${days} more days`
}

/**
 * Why there is no bank to choose, or nothing when there is one.
 *
 * A market with no banks and a list that never arrived reach this page looking
 * identical — the server folds "could not be reached" into the same `attention`
 * sentence it uses for a lapsed consent — so an empty list that arrived with
 * something needing attention is taken for the failure. A box that invites a
 * search and answers every one of them with "no banks match" is the worse of
 * the two mistakes: it reads as the bank not existing rather than as an outage,
 * and there is nothing on the page to press.
 */
function nothingToChooseFrom(
  source: ConsentSource,
  banks: readonly ConsentSource['banks'][number][],
  failed: boolean,
  loading: boolean,
): string | undefined {
  if (banks.length > 0) return undefined
  if (loading) return 'Reading the banks in this country…'
  // Only the country the page arrived with has had its failure described above
  // by the server. A country chosen afterwards fails in its own request, and
  // pointing at an explanation that is not there reads as a missing sentence.
  if (failed) {
    return 'The banks in this country could not be read just now, so there is nothing to search. Try again, or choose another country.'
  }
  if (source.attention !== undefined) {
    return 'The list of banks could not be read, so there is nothing to search yet — what went wrong is said above.'
  }
  return 'No banks are on offer for this country yet, so there is nothing to search.'
}

/**
 * A country's name in the reader's own language, falling back to the code.
 *
 * `Intl` already knows all twenty-nine of these in every locale the dashboard
 * is read in, and a table of names kept here would be a second thing to
 * translate and a second thing to let drift. Unlike a figure, a country's name
 * is about the reader and not about the money, so the reader's locale decides
 * it outright.
 *
 * Built once: the menu asks for all twenty-nine of them on every keystroke.
 */
let regionNames: Intl.DisplayNames | undefined | null

function countryName(code: string): string {
  if (regionNames === undefined) {
    try {
      const locale = typeof navigator === 'undefined' ? 'en' : navigator.language || 'en'
      regionNames = new Intl.DisplayNames([locale], { type: 'region' })
    } catch {
      regionNames = null
    }
  }
  return regionNames?.of(code) ?? code
}

/**
 * A bank that is already connected is offered again without comment. A
 * household that banks personally and through a company holds two consents at
 * the same bank, and anything that greys the second one out tells them their
 * own arrangement is a mistake.
 *
 * The second menu is filled from the chosen bank's own answer, and empties when
 * the bank changes under it: a consent kind a bank does not grant is refused,
 * and a stale answer left standing from the previous bank is the same mistake
 * with nobody to blame for it.
 */
function ConnectBank({ source }: { source: ConsentSource }): ReactElement {
  const start = useStartConsent(source.id)
  const [country, setCountry] = useState(source.country)
  const [bank, setBank] = useState('')
  const [holder, setHolder] = useState('')

  const initial = useMemo(
    () => ({ country: source.country, banks: source.banks }),
    [source.country, source.banks],
  )
  const listing = useBanksIn(source.id, country, initial)
  const banks = listing.data ?? []

  const countries = useMemo(
    () =>
      source.countries
        .map((code) => ({ value: code, label: countryName(code) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [source.countries],
  )
  const choices = useMemo(
    () =>
      banks.map((option) => ({
        value: option.name,
        label: option.beta ? `${option.name} (beta)` : option.name,
      })),
    [banks],
  )
  const missing = nothingToChooseFrom(source, banks, listing.isError, listing.isLoading)
  const chosen = banks.find((candidate) => candidate.name === bank)
  const holders = holdersOf(banks, bank)

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    if (chosen === undefined || holder === '') return
    // The bank's own country rather than the menu's: they agree, and the one
    // that was read from the provider is the one `/auth` will be aimed with.
    start.mutate({ bank: chosen.name, country: chosen.country, psuType: holder })
  }

  return (
    <form onSubmit={submit} aria-label="Connect a bank">
      <fieldset>
        <Combobox
          id={`${source.id}-country`}
          label="Country"
          name="country"
          noun="countries"
          placeholder="Type to find the country"
          options={countries}
          value={country}
          onChange={(next) => {
            setCountry(next)
            // A bank belongs to the country it was listed under, so an answer
            // left standing from the previous one would start an authorization
            // against a bank nobody chose in a market nobody picked.
            setBank('')
            setHolder('')
          }}
        />
        <Combobox
          id={`${source.id}-bank`}
          label="Bank"
          name="bank"
          noun="banks"
          placeholder="Type to find your bank"
          options={choices}
          value={bank}
          onChange={(next) => {
            setBank(next)
            setHolder('')
          }}
          {...(missing === undefined ? {} : { unavailable: missing })}
        />
        <label htmlFor={`${source.id}-holder`}>
          Whose accounts
          <select
            id={`${source.id}-holder`}
            value={holder}
            onChange={(e) => setHolder(e.target.value)}
          >
            <option value="">Choose…</option>
            {holders.map((option) => (
              <option key={option} value={option}>
                {option === 'business' ? 'Business' : 'Personal'}
              </option>
            ))}
          </select>
          <small>Personal and business are separate approvals, even at the same bank.</small>
        </label>
      </fieldset>

      {start.error && <p className="nw-refusal">{start.error.message}</p>}

      <div className="nw-actions">
        <Button
          type="submit"
          kind="primary"
          disabled={start.isPending || chosen === undefined || holder === ''}
        >
          {start.isPending ? 'Opening your bank…' : 'Continue at your bank'}
        </Button>
      </div>
    </form>
  )
}

/**
 * An approval that was granted and never attributed, boxed above what is
 * connected. Each one cost a trip through MitID, and an account nobody has
 * claimed collects nothing and says nothing.
 *
 * On the page rather than behind a dialog: it is the one thing here that is
 * already paid for and still unfinished.
 */
function Attribution({
  source,
  pending,
}: {
  source: ConsentSource
  pending: PendingConsent
}): ReactElement {
  const attribute = useAttributeAccounts(source.id)
  const [choices, setChoices] = useState<Record<string, AttributionChoice>>({})
  const accounts = pending.accounts
  const complete = isFullyAttributed(accounts, choices)

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    // Nothing is sent until every account has been answered. An account
    // attributed to the wrong entity is taxed at the wrong rate, and the one
    // figure a sole owner reads is unchanged either way, so a guess is a wrong
    // total with nothing on screen to point at.
    if (!complete) return
    attribute.mutate({
      reference: pending.reference,
      accounts: accounts.map((account) => attributionOf(choices[account.uid] ?? '', account.uid)),
    })
  }

  return (
    <form
      id={attributionId(pending.reference)}
      className="nw-waiting"
      onSubmit={submit}
      aria-label="Whose accounts are these"
    >
      <h3>
        {pending.bank} returned {accounts.length} accounts
      </h3>
      <p className="nw-lede">
        Say whose money each one holds. Nothing is guessed: an account put under the wrong name is
        taxed at the wrong rate, and the total still looks right.
      </p>

      {/* Said rather than left as a menu with nothing in it. The approval is
          already given and stays waiting, so the fix is to add an entity and
          come back — not another trip through MitID. */}
      {source.entities.length === 0 && (
        <p className="nw-note">
          This household has no entities yet, so there is nothing to attribute these accounts to.
          The approval stays here until there is.
        </p>
      )}

      <DataTable columns={ATTRIBUTION_COLUMNS} label="Accounts waiting to be attributed">
        {accounts.map((account) => {
          const name = account.name ?? account.iban ?? account.uid
          return (
            <tr key={account.uid}>
              <td data-label="Account">
                {name}
                <br />
                <small>
                  {account.currency}
                  {account.iban === undefined ? '' : ` · ${account.iban}`}
                </small>
                {/* Shown, never pre-selected. It is the owner's own earlier
                    answer about this same account across a renewal, and an
                    answer that fills itself in is the bug this step exists
                    to stop. */}
                {previously(account.previousEntityKey, account.previousExcluded, source) && (
                  <>
                    <br />
                    <small>
                      Last time:{' '}
                      {previously(account.previousEntityKey, account.previousExcluded, source)}
                    </small>
                  </>
                )}
              </td>
              <td data-label="Whose is it">
                <select
                  aria-label={`Whose is ${name}?`}
                  value={choices[account.uid] ?? ''}
                  onChange={(e) =>
                    setChoices((current) => ({ ...current, [account.uid]: e.target.value }))
                  }
                >
                  <option value="">Choose…</option>
                  {source.entities.map((entity) => (
                    <option key={entity.key} value={entity.key}>
                      {entity.name}
                    </option>
                  ))}
                  <option value={DONT_TRACK}>Don’t track this account</option>
                </select>
              </td>
            </tr>
          )
        })}
      </DataTable>

      {attribute.error && <p className="nw-refusal">{attribute.error.message}</p>}
      {attribute.data?.collection !== undefined && (
        <p className="nw-collected" role="status">
          {attribute.data.collection.note}
        </p>
      )}

      <div className="nw-actions">
        <Button type="submit" kind="primary" disabled={!complete || attribute.isPending}>
          {attribute.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}

/** What this household said about this account before the renewal, in its words. */
function previously(
  entityKey: string | undefined,
  excluded: boolean | undefined,
  source: ConsentSource,
): string | undefined {
  // An account that was excluded and also once carried an entity is an account
  // somebody deliberately stopped tracking, so the exclusion is what is said.
  if (excluded === true) return 'not tracked'
  if (entityKey === undefined) return undefined
  return source.entities.find((entity) => entity.key === entityKey)?.name ?? entityKey
}
